/**
 * ISO 8601 weeks of UTC day numbers (days since 1970-01-01), by whole-number arithmetic only (no Date in the domain):
 * the event rotation's instance ids (`<kind>_<ISO year>w<ISO week>`, meta/eventSchedule.ts) and their pruning.
 */

const DAYS_PER_WEEK = 7;
/** 1970-01-01 was a Thursday: three days after a Monday. */
const THURSDAY_SHIFT = 3;

/** 0 = Monday … 6 = Sunday. */
export function isoWeekday(day: number): number { return (((day + THURSDAY_SHIFT) % DAYS_PER_WEEK) + DAYS_PER_WEEK) % DAYS_PER_WEEK; }

/** The Monday of the week holding `day`. */
export function mondayOf(day: number): number { return day - isoWeekday(day); }

/** The civil year of a UTC day number (Howard Hinnant's days-to-civil, integer only). */
function yearOf(day: number): number {
  const z = day + 719468, era = Math.floor(z / 146097), doe = z - era * 146097;
  const yoe = Math.floor((doe - Math.floor(doe / 1460) + Math.floor(doe / 36524) - Math.floor(doe / 146096)) / 365);
  const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100));
  const mp = Math.floor((5 * doy + 2) / 153), m = mp < 10 ? mp + 3 : mp - 9;
  return yoe + era * 400 + (m <= 2 ? 1 : 0);
}

/** Days since 1970-01-01 of 1 January of `y`. */
function jan1(y: number): number {
  const yy = y - 1, era = Math.floor(yy / 400), yoe = yy - era * 400, doy = 306; // 1 January = day 306 of the March-based year
  return era * 146097 + yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy - 719468;
}

/** ISO 8601 [year, week] of the week holding UTC day `day`: the year of that week's Thursday. */
export function isoWeek(day: number): [number, number] {
  const thursday = mondayOf(day) + THURSDAY_SHIFT;
  const y = yearOf(thursday);
  return [y, Math.floor((thursday - jan1(y)) / DAYS_PER_WEEK) + 1];
}

/** The Monday of ISO week `week` of ISO year `year` (week 1 holds 4 January). */
export function isoWeekMonday(year: number, week: number): number {
  return mondayOf(jan1(year) + THURSDAY_SHIFT) + (week - 1) * DAYS_PER_WEEK;
}

/** "2026w42": the ISO week tag of a rotation instance id. */
export function isoWeekTag(day: number): string {
  const [y, w] = isoWeek(day);
  return `${y}w${w < 10 ? "0" : ""}${w}`;
}
